import React, { useState, useEffect } from 'react';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Spinner,
    Alert
} from 'reactstrap';
import { checkPaymentStatus, extractInvoiceLinks, getPaymentErrorMessage } from '../../services/paymentService';

const PaymentRetryModal = ({
    isOpen,
    toggle,
    shipmentId,
    shipmentFriendlyId,
    shipmentData,
    onSuccess,
    onClose,
    fromOrderCreation = false // Flag to indicate if modal opened from order creation
}) => {
    const [checking, setChecking] = useState(false);
    const [statusMessage, setStatusMessage] = useState(null);
    const [messageType, setMessageType] = useState('info'); // 'success', 'warning', 'danger', 'info'
    const [invoiceLinks, setInvoiceLinks] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        if (isOpen && shipmentData) {
            // Extract invoice links from provided data (billing_details is now in GraphQL)
            const links = extractInvoiceLinks(shipmentData);
            setInvoiceLinks(links);

            const error = getPaymentErrorMessage(shipmentData);
            setErrorMessage(error);

            // Reset status message
            setStatusMessage(null);
            setMessageType('info');
        }
    }, [isOpen, shipmentData]);

    const handleCheckPaymentStatus = async () => {
        if (!shipmentId) {
            setStatusMessage('Shipment ID not found. Please try again.');
            setMessageType('danger');
            return;
        }

        try {
            setChecking(true);
            setStatusMessage(null);

            const response = await checkPaymentStatus(shipmentId);

            const responseLinks = extractInvoiceLinks({
                billing_details: response && response.payload ? response.payload.billing_details : null,
                invoicing_errors: response && response.payload ? response.payload.invoicing_errors : null,
            });

            if (responseLinks) {
                setInvoiceLinks(responseLinks);
            }

            if (response.status === 'successful' && response.payload && response.payload.paid) {
                // Payment confirmed!
                setStatusMessage('Payment confirmed! Your order is now active.');
                setMessageType('success');

                // Call success callback after short delay to show success message
                setTimeout(() => {
                    toggle(); // Close modal first
                    if (onSuccess) {
                        onSuccess(response);
                    }
                }, 1500);
            } else if (response.status === 'unavailable' && response.payload && response.payload.endpointUnsupported) {
                setStatusMessage('Payment verification endpoint is not available in this environment yet. Please refresh Orders in 30-60 seconds or check in Admin, then return here.');
                setMessageType('warning');
            } else {
                // Payment not yet received
                setStatusMessage(
                    response && response.payload && response.payload.reasonMessage
                        ? response.payload.reasonMessage
                        : 'Payment not received yet. Please complete payment and try again.'
                );
                setMessageType('warning');
            }
        } catch (error) {
            // Check if it's a 409 Conflict (invoice not paid yet)
            if (error.response && error.response.status === 409) {
                setStatusMessage('Payment not received yet. Please complete payment and try again.');
                setMessageType('warning');
            } else {
                setStatusMessage('Failed to check payment status. Please try again.');
                setMessageType('danger');
            }
        } finally {
            setChecking(false);
        }
    };

    const handlePayInvoice = () => {
        if (invoiceLinks && invoiceLinks.hostedUrl) {
            // Open hosted invoice URL in new tab
            window.open(invoiceLinks.hostedUrl, '_blank', 'noopener,noreferrer');
        }
    };

    const canPayInvoice = Boolean(invoiceLinks && invoiceLinks.hostedUrl);
    const canDownloadPdf = Boolean(invoiceLinks && invoiceLinks.pdfUrl);

    const handleClose = () => {
        if (onClose) {
            onClose();
        }
        toggle();
        // Only reload page if modal was opened from order creation
        if (fromOrderCreation) {
            window.location.reload();
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={handleClose} size="lg" centered backdrop="static">
            <ModalHeader toggle={handleClose}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <i className="fas fa-exclamation-circle text-warning" style={{ fontSize: '2rem', marginRight: '15px' }}></i>
                    <span style={{ fontSize: '1.75rem', fontWeight: '600' }}>Payment Required</span>
                </div>
            </ModalHeader>
            <ModalBody>
                <Alert color="info">
                    <strong>Order Created:</strong> Your order #{shipmentFriendlyId} has been created
                    but requires payment to proceed.
                </Alert>

                {errorMessage && (
                    <div className="mb-3">
                        <p style={{ fontSize: '14px', color: '#525f7f' }}>
                            <strong>Issue:</strong> {errorMessage}
                        </p>
                    </div>
                )}

                <div className="mb-3">
                    <p style={{ fontSize: '14px', color: '#525f7f' }}>
                        Please complete payment to activate your order.
                        {canPayInvoice
                            ? ' Click "Pay Invoice" to open the payment page in a new tab.'
                            : ' We could not find a payment URL on this response yet. Click "Check Payment Status" to refresh invoice details.'}
                    </p>
                </div>

                {invoiceLinks && (
                    <div className="mb-3">
                        <h6 style={{ color: '#525f7f', marginBottom: '12px', fontWeight: '700' }}>INVOICE:</h6>
                        <div className="d-flex gap-2" style={{ gap: '12px' }}>
                            {invoiceLinks.hostedUrl && (
                                <Button
                                    color="primary"
                                    onClick={handlePayInvoice}
                                    style={{ padding: '10px 20px' }}
                                >
                                    <i className="fas fa-external-link-alt" style={{ marginRight: '10px' }}></i>
                                    Pay Invoice
                                </Button>
                            )}
                            {invoiceLinks.pdfUrl && (
                                <a
                                    href={invoiceLinks.pdfUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-outline-secondary"
                                    style={{ padding: '10px 20px' }}
                                >
                                    <i className="fas fa-file-pdf" style={{ marginRight: '10px' }}></i>
                                    Download PDF
                                </a>
                            )}
                        </div>
                    </div>
                )}

                {!canPayInvoice && !canDownloadPdf && (
                    <Alert color="warning" className="mt-3 mb-0">
                        <i className="fas fa-link" style={{ marginRight: '8px' }}></i>
                        No invoice link is attached yet. This usually means invoice details have not propagated to shipment billing details yet, or the backend returned an error without a hosted invoice URL. If this persists after checking status, close this dialog, choose a different card, and resubmit.
                    </Alert>
                )}

                {statusMessage && (
                    <Alert color={messageType} className="mt-3">
                        {messageType === 'success' && <i className="fas fa-check-circle" style={{ marginRight: '8px' }}></i>}
                        {messageType === 'warning' && <i className="fas fa-exclamation-triangle" style={{ marginRight: '8px' }}></i>}
                        {messageType === 'danger' && <i className="fas fa-times-circle" style={{ marginRight: '8px' }}></i>}
                        {statusMessage}
                    </Alert>
                )}

                <div className="mt-3">
                    <p style={{ fontSize: '12px', color: '#8898aa', fontStyle: 'italic' }}>
                        After completing payment, return to this page and click "Check Payment Status" to
                        verify and activate your order.
                    </p>
                </div>
            </ModalBody>
            <ModalFooter style={{ gap: '10px' }}>
                <Button
                    color="secondary"
                    onClick={handleClose}
                    disabled={checking}
                    style={{ padding: '10px 20px' }}
                >
                    Close
                </Button>
                <Button
                    color="success"
                    onClick={handleCheckPaymentStatus}
                    disabled={checking}
                    style={{ padding: '10px 20px' }}
                >
                    {checking ? (
                        <>
                            <Spinner size="sm" style={{ marginRight: '10px' }} />
                            Checking...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-sync-alt" style={{ marginRight: '10px' }}></i>
                            Check Payment Status
                        </>
                    )}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default PaymentRetryModal;
